import { useCallback, useRef, type ReactNode } from 'react';
import './AuthPage.css';

/**
 * Общая сцена экранов до входа: фон и карточка. Ею пользуются страница входа
 * и регистрация по ссылке — человек проходит их подряд, и выглядеть они
 * должны одинаково.
 */
export function AuthScene({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<number | null>(null);

  /*
   * Подсветка под курсором. Координаты пишутся прямо в CSS-переменные, минуя
   * состояние: перерисовывать React на каждом движении мыши — это десятки
   * лишних рендеров в секунду. Кадр запрашивается один, лишние события
   * движения между кадрами отбрасываются.
   */
  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const { clientX, clientY } = event;
    if (frameRef.current !== null) return;

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const node = rootRef.current;
      if (!node) return;
      node.style.setProperty('--mx', `${clientX}px`);
      node.style.setProperty('--my', `${clientY}px`);
    });
  }, []);

  return (
    <div className="auth" ref={rootRef} onPointerMove={onPointerMove}>
      {/* Слои фона: свет, сетка, зерно, подсветка под курсором, виньетка. */}
      <div className="auth__aurora" aria-hidden="true">
        <span className="auth__blob auth__blob--1" />
        <span className="auth__blob auth__blob--2" />
        <span className="auth__blob auth__blob--3" />
      </div>
      <div className="auth__grid" aria-hidden="true" />
      <div className="auth__grain" aria-hidden="true" />
      <div className="auth__spot" aria-hidden="true" />
      <div className="auth__vignette" aria-hidden="true" />

      <div className={wide ? 'auth__stage auth__stage--wide' : 'auth__stage'}>
        <div className="auth__halo" aria-hidden="true" />
        <div className="auth__card">{children}</div>
      </div>
    </div>
  );
}
